// Types
import type {
  ManageBookingOriginalOrderItem,
  OriginalSearchCriteria,
} from '@white-label-types/parking-booking';
import type {
  TravelInsuranceQuoteDetails,
  Traveller,
} from '@white-label-types/travel-insurance';
import type { Item as ReceiptOrderItem } from '@white-label-types/parking-checkout';

import { PRODUCT_TYPES } from '@white-label-configuration/constants';

/**
 * Parses quote details from a travel insurance manage booking order item.
 * @param item a manage booking order item
 * @returns travel insurance quote details
 */
export const parseTravelInsuranceQuoteDetails = (
  item: ManageBookingOriginalOrderItem | ReceiptOrderItem,
): TravelInsuranceQuoteDetails | null => {
  if (item.product_code !== PRODUCT_TYPES.TRAVEL_INSURANCE) return null;

  // Determine search criteria - could be under different key based on `/booking/management` or `/order/receipt` responses
  const searchCriteria: OriginalSearchCriteria['travel_insurance'] =
    'search_criteria' in item
      ? item.search_criteria.travel_insurance
      : item.order_criteria?.travel_insurance;
  if (!searchCriteria) return null;

  const { destinations = [], documents = [], policy_number = '' } = item.product_type_data || {};
  const { travellers = [] } = item;

  const addOns = item.inventory_option.sub_options
    ?.map(({ id, details }) => ({
      id: `${id}`,
      name: details.name || '',
    }))
    .filter(({ id }) => id !== 'base_package') ?? [];

  return {
    policyNumber: policy_number,
    startDate: searchCriteria?.date1,
    endDate: searchCriteria?.date2,
    destination: destinations.map((dest) => dest.name),
    travellers: parseTravellers(travellers),
    documents: documents.map(({ name, url }) => ({ name, url })),
    addOns,
  };
}

function parseTravellers(travellers: Traveller[]): TravelInsuranceQuoteDetails['travellers'] {
  const leadTraveller = travellers.find(traveller => traveller.is_lead);
  const additionalTravellers = travellers.filter(traveller => !traveller.is_lead);

  if (!leadTraveller) return [];

  const parseTraveller = (traveller: Traveller) => ({
    countryOfResidence: traveller?.country_of_residence?.name ?? null,
    dateOfBirth: traveller?.user?.date_of_birth ?? null,
    name: `${traveller?.user?.first_name} ${traveller?.user?.last_name}`,
  });

  return [leadTraveller, ...additionalTravellers].map(parseTraveller);
}
